import React, { useState, useEffect } from "react";
import axios from "axios";
import Navbar from "../components/Navbar"; // Import Navbar
import { AiOutlineCloseCircle } from "react-icons/ai"; // Close icon for modal

const Booking = () => {
  const [bookings, setBookings] = useState([]);
  const [userDetailsMap, setUserDetailsMap] = useState({}); // Map to store user details by userId
  const [selectedBooking, setSelectedBooking] = useState(null); // State for the selected booking
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("Pending"); // State for active tab

  // Fetch booking details from the API
  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const response = await axios.get("https://shatabackend.in/bookings");
        setBookings(response.data); // Assuming the API returns an array of bookings
        fetchAllUserDetails(response.data); // Fetch user details for all bookings
      } catch (error) {
        console.error("Error fetching bookings:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, []);

  // Fetch user details for all bookings
  const fetchAllUserDetails = async (bookings) => {
    const userDetailsPromises = bookings.map(async (booking) => {
      if (booking.userId && !userDetailsMap[booking.userId]) {
        try {
          const response = await axios.get(
            `https://shatabackend.in/user/${booking.userId}`
          );
          return { userId: booking.userId, details: response.data };
        } catch (error) {
          console.error(`Error fetching user details for ${booking.userId}:`, error);
          return { userId: booking.userId, details: null };
        }
      }
      return null;
    });

    const userDetailsArray = await Promise.all(userDetailsPromises);
    const newUserDetailsMap = { ...userDetailsMap };

    userDetailsArray.forEach((userDetails) => {
      if (userDetails) {
        newUserDetailsMap[userDetails.userId] = userDetails.details;
      }
    });

    setUserDetailsMap(newUserDetailsMap);
  };

  // Function to handle card click
  const handleCardClick = (booking) => {
    setSelectedBooking(booking); // Set the selected booking for the modal
  };

  // Function to close the modal
  const closeModal = () => {
    setSelectedBooking(null); // Clear the selected booking
  };

  // Filter bookings based on the active tab
  const filteredBookings = bookings.filter((booking) => {
    const today = new Date();
    const dateFrom = new Date(booking.dateFrom);

    if (activeTab === "Pending") {
      return booking.status === "pending";
    } else if (activeTab === "Visited") {
      return booking.status === "visited";
    } else if (activeTab === "Completed") {
      return (
        (booking.status === "confirmEvent" || booking.status === "completed") &&
        dateFrom < today
      );
    } else if (activeTab === "In Progress") {
      return (
        (booking.status === "confirmEvent" || booking.status === "completed") &&
        dateFrom.toDateString() === today.toDateString()
      );
    } else if (activeTab === "Upcoming") {
      return (
        (booking.status === "confirmEvent" || booking.status === "completed") &&
        dateFrom > today
      );
    }
    return false;
  });

  if (loading) {
    return <div>Loading...</div>; // Show a loading message while fetching data
  }

  return (
    <div className="min-h-screen bg-white ">
      <Navbar /> {/* Render Navbar */}
      <div className="container mx-auto py-6">
        <h1 className="text-2xl font-bold mb-6 text-center items-center justify-center">Booking Details</h1>

        {/* Tabs */}
        <div className="flex mb-6 text-center items-center justify-center">
          {["Pending", "Visited", "Completed", "In Progress", "Upcoming"].map(
            (tab) => (
              <button
                key={tab}
                className={`px-4 rounded-full ${
                  activeTab === tab
                    ? "bg-black text-white "
                    : " text-black "
                }`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            )
          )}
        </div>

        {/* Booking Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredBookings.map((booking, index) => {
            const userDetails = userDetailsMap[booking.userId];
            const hasValidUserDetails =
              userDetails && userDetails.fullName && userDetails.phone;

            return (
              <div
                key={index}
                className="border rounded-lg p-6 shadow-md bg-white cursor-pointer"
                onClick={() => handleCardClick(booking)}
              >
                <h2 className="text-lg font-bold mb-2">
                    Service Name: {booking.eventType || "No Event Name"}
                </h2>
                <p className="text-sm text-black">
                  Booking ID: {booking._id || "N/A"}
                </p>
                {hasValidUserDetails ? (
                  <>
                    <p className="text-sm text-black">
                      User Name: {userDetails.fullName}
                    </p>
                    <p className="text-sm text-black">
                      Phone: {userDetails.phone}
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-black">No User Details</p>
                )}
                <p className="text-sm text-black">
                  Partner Name: {booking.partnerName || "No Partner Name"}
                </p>
                <p className="text-sm text-black">
                  Status: {booking.status || "N/A"}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modal for Booking Details */}
      {selectedBooking && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-96 relative">
            <button
              onClick={closeModal}
              className="absolute top-2 right-2 text-orange-500 hover:text-orange-600 text-2xl"
            >
              <AiOutlineCloseCircle /> {/* Close icon */}
            </button>
            <h2 className="text-lg font-bold mb-4">
              Event: {selectedBooking.eventType || "No Event Name"}
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Booking ID: {selectedBooking._id || "N/A"}
            </p>
            {userDetailsMap[selectedBooking.userId] ? (
              <>
                <h3 className="text-md font-semibold mb-2">User Details:</h3>
                <p className="text-sm text-gray-600">
                  Name: {userDetailsMap[selectedBooking.userId].fullName || "No Name"}
                </p>
                <p className="text-sm text-gray-600">
                  Email: {userDetailsMap[selectedBooking.userId].email || "No Email"}
                </p>
                <p className="text-sm text-gray-600">
                  Phone: {userDetailsMap[selectedBooking.userId].phone || "No Phone"}
                </p>
              </>
            ) : (
              <p className="text-sm text-gray-600">Loading user details...</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Booking;